import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Store, Settings, AlertCircle, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductSelect: (product: 'theme' | 'manager', shopifyUrl?: string) => void;
}

const SupportModal: React.FC<SupportModalProps> = ({
  isOpen,
  onClose,
  onProductSelect
}) => {
  const [stores, setStores] = useState<{ url: string; active: boolean; }[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user } = useAuthStore();

  useEffect(() => {
    const fetchStores = async () => {
      if (user?.email) {
        try {
          const response = await fetch('https://services.shopiweb.fr/api/licenses/get_by_email/premium', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({ email: user.email }).toString()
          });

          if (response.ok) {
            const data = await response.json();
            const userStores = data.licenses.map((license: any) => ({
              url: license.myshopifyurl,
              active: license.active
            }));
            setStores(userStores);
          }
        } catch (error) {
          console.error('Error fetching stores:', error);
          toast.error('Erreur lors de la récupération des boutiques');
        }
      }
    };

    if (isOpen) {
      fetchStores();
    }
  }, [user?.email, isOpen]);

  const handleThemeSelect = () => {
    if (stores.length === 0) {
      toast.error('Vous n\'avez aucune boutique Shopify associée à votre compte');
      return;
    }
    setIsDropdownOpen(true);
  };

  const handleStoreSelect = (url: string) => {
    setIsDropdownOpen(false);
    onProductSelect('theme', url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full relative overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Créer un ticket de support
          </h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Quel produit concerne votre demande ?
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleThemeSelect}
              className="flex flex-col items-center p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/50 dark:to-blue-900/50 rounded-xl border border-purple-100 dark:border-purple-800 hover:shadow-lg transition-all"
            >
              <Store className="w-12 h-12 text-purple-600 dark:text-purple-400 mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Thème Shopiweb Premium
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                Questions sur l'installation, la configuration ou l'utilisation du thème
              </p>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onProductSelect('manager')}
              className="flex flex-col items-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl border border-blue-100 dark:border-blue-800 hover:shadow-lg transition-all"
            >
              <Settings className="w-12 h-12 text-blue-600 dark:text-blue-400 mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                App Shopiweb Manager
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                Questions sur la gestion des licences ou l'interface d'administration
              </p>
            </motion.button>
          </div>

          {/* Store Selection Dropdown */}
          {isDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg overflow-hidden"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sélectionnez votre boutique
                </h4>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {stores.map((store) => (
                  <button
                    key={store.url}
                    onClick={() => handleStoreSelect(store.url)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center">
                      <Store className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-900 dark:text-white">
                        {store.url}
                      </span>
                    </div>
                    <span className={`text-sm ${
                      store.active 
                        ? 'text-green-500 dark:text-green-400' 
                        : 'text-red-500 dark:text-red-400'
                    }`}>
                      {store.active ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <div className="mt-6 bg-blue-50 dark:bg-blue-900/50 border border-blue-100 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Sélectionnez le produit concerné pour nous permettre de mieux vous aider.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SupportModal;